import { ref, computed } from 'vue'

const WEIGHT_KEY = 'banjino_weight_unit'
const VOLUME_KEY = 'banjino_volume_unit'
const DISPLAY_MODE_KEY = 'banjino_display_mode'

// Module-level singletons (same pattern as useSnackbar.js)
const weightUnit = ref(localStorage.getItem(WEIGHT_KEY) || 'LB')
const volumeUnit = ref(localStorage.getItem(VOLUME_KEY) || 'GAL')
const displayMode = ref(localStorage.getItem(DISPLAY_MODE_KEY) || 'absolute')

/**
 * Composable for managing per-category unit preferences.
 * Weight and volume can be set independently (e.g. lb + L, or kg + gal).
 * Persisted to localStorage. Shared across all components via module-level refs.
 */
export function useUnitPreference() {
  // Convenience computed for the common "all metric" / "all imperial" cases
  const isMetric = computed(() => weightUnit.value === 'KG' && volumeUnit.value === 'L')
  const isImperial = computed(() => weightUnit.value === 'LB' && volumeUnit.value === 'GAL')

  function setWeightUnit(code) {
    weightUnit.value = code
    localStorage.setItem(WEIGHT_KEY, code)
  }

  function setVolumeUnit(code) {
    volumeUnit.value = code
    localStorage.setItem(VOLUME_KEY, code)
  }

  function setDisplayMode(mode) {
    displayMode.value = mode
    localStorage.setItem(DISPLAY_MODE_KEY, mode)
  }

  /** Set both at once (preset convenience) */
  function setPreset(preset) {
    if (preset === 'imperial') {
      setWeightUnit('LB')
      setVolumeUnit('GAL')
    } else if (preset === 'metric') {
      setWeightUnit('KG')
      setVolumeUnit('L')
    }
  }

  /**
   * Returns the preferred unit code for a given category.
   *
   * @param {'weight'|'volume'|'count'} category
   * @returns {string|null} unit code (e.g. 'LB', 'KG', 'GAL', 'L')
   */
  function preferredUnitCode(category) {
    if (category === 'weight') return weightUnit.value
    if (category === 'volume') return volumeUnit.value
    return null // count units have no system preference
  }

  /**
   * Returns a human-readable name for the current preference of a category.
   */
  function preferredUnitName(category) {
    const map = {
      LB: 'Pounds', KG: 'Kilograms', OZ: 'Ounces', G: 'Grams',
      GAL: 'Gallons', L: 'Liters', ML: 'Milliliters', FL_OZ: 'Fluid Ounces'
    }
    const code = preferredUnitCode(category)
    return code ? (map[code] || code) : null
  }

  return {
    weightUnit,
    volumeUnit,
    displayMode,
    isMetric,
    isImperial,
    setWeightUnit,
    setVolumeUnit,
    setDisplayMode,
    setPreset,
    preferredUnitCode,
    preferredUnitName
  }
}
